import React, { useState } from "react";
import { Mail, ArrowLeft, Send, CheckCircle, Key } from "lucide-react";
import { apiClient } from "../../services/api";
import { useTranslation } from "react-i18next";

const ForgotPasswordScreen = ({ onBack }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("Please enter your email address");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post("/auth/reset-password", { email });
      if (response.data.success) setEmailSent(true);
    } catch (err) {
      setError(
        err.response?.data?.error || "Failed to send reset email. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Glass card style (from login page)
  const glassCardStyles = `
    .glass-modal-card {
      background-color: rgba(255,255,255,0.1);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      position: relative;
      border-radius: 1.5rem;
      isolation: isolate;
      box-shadow:
        0 0 20px rgba(56,189,248,0.65),
        0 0 45px rgba(59,130,246,0.45),
        0 0 90px rgba(191,219,254,0.25),
        0 0 140px rgba(14,165,233,0.15);
      border: 1px solid rgba(255,255,255,0.2);
    }
    .glass-modal-card::before {
      content:'';
      position:absolute;
      inset:-32px;
      border-radius:inherit;
      background:radial-gradient(circle at 50% 50%, rgba(14,165,233,0.55) 0%, rgba(37,99,235,0.35) 38%, rgba(15,23,42,0) 70%);
      opacity:0.85;
      filter: blur(2px);
      z-index:-1;
      animation: portalPulse 3.6s ease-in-out infinite alternate;
    }
    .glass-modal-card::after {
      content:'';
      position:absolute;
      inset:-18px;
      border-radius:inherit;
      border:1px solid rgba(125,211,252,0.35);
      background: radial-gradient(circle, rgba(125,211,252,0.15) 20%, transparent 70%);
      z-index:-1;
      opacity:0.55;
      filter: blur(0.5px);
    }
    @keyframes portalPulse {
      0% { opacity:0.8; filter: blur(1.5px); }
      100% { opacity:0.95; filter: blur(2.4px); }
    }
  `;

  // Success screen
  if (emailSent) {
    return (
      <div className="w-full  min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 flex  items-center justify-center p-4 overflow-y-auto">
        <style>{glassCardStyles}</style>
        <div className="w-full max-w-md glass-modal-card p-8 space-y-6">
          <div className="text-center space-y-4">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-white">Check your email</h1>
            <p className="text-white/80">We sent a password reset link to</p>
            <p className="text-white font-semibold">{email}</p>
          </div>

          <div className="space-y-3">
            {["Open the email we just sent you", "Click the password reset link", "Create your new password"].map(
              (text, idx) => (
                <div key={idx} className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-blue-600/20 flex items-center justify-center mt-0.5">
                    <span className="text-blue-300 text-sm font-bold">{idx + 1}</span>
                  </div>
                  <p className="text-white/80">{text}</p>
                </div>
              )
            )}
          </div>

          <p className="text-white/60 text-sm text-center">
            Didn't receive the email?{" "}
            <button
              onClick={() => setEmailSent(false)}
              className="text-blue-300 hover:text-white font-medium"
            >
              {t('common.try_again')}
            </button>
          </p>

          <button
            onClick={onBack}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-2xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
          >
            {t('auth.sign_in_link')}
          </button>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 flex  items-center justify-center p-4 overflow-y-auto">
      <style>{glassCardStyles}</style>
      <div className="w-full max-w-md glass-modal-card p-8 space-y-6">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-2">
            <Key className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">{t('auth.forgot_password')}</h1>
          <p className="text-white/80">Enter your email and we'll send you a reset link</p>
        </div>

        {error && (
          <div className="p-4 rounded-2xl bg-rose-500/15 border border-rose-200/60 text-rose-50/90 backdrop-blur-xl shadow-[0_12px_40px_rgba(225,29,72,0.35)]">
            <p className="text-sm font-semibold">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-white/90 mb-2">
              {t('auth.email_label')}
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <Mail className="w-5 h-5 text-cyan-100/80" />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                className="w-full pl-12 pr-4 py-3 rounded-2xl border border-white/25 bg-white/10 text-white placeholder-white/70 backdrop-blur-xl focus:outline-none focus:ring-2 focus:ring-cyan-200 transition-all"
                placeholder={t('auth.email_label')}
                disabled={loading}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-2xl border border-white/25 bg-gradient-to-br from-cyan-300/25 via-sky-500/20 to-blue-700/30 text-sky-50 py-3 px-4 font-semibold backdrop-blur-2xl shadow-[0_20px_60px_rgba(14,165,233,0.35)] transition-all hover:scale-[1.01] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/80 border-t-transparent"></div>
                <span>{t('common.loading')}</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Send Reset Link</span>
              </>
            )}
          </button>

          <button
            onClick={onBack}
            className="w-full flex items-center justify-center space-x-2 text-white/70 hover:text-white transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('auth.sign_in_link')}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordScreen;
